'use client';

import { getToolAuthToken } from '@/lib/browser-tool-usage';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';

type OutputFormat = 'png' | 'jpeg' | 'webp';
type ActionMode = 'blur' | 'pixelate' | 'black' | 'white' | 'crop';
type EditorInteractionMode = 'select' | 'move';
type ScreenshotRedactorVariant = {
  lockedScreenshotActionMode?: ActionMode;
  defaultScreenshotActionMode?: ActionMode;
  hideActionChooser?: boolean;
  badgeLabel?: string;
  toolSlug?: string;
};

type LoadedImage = {
  element: HTMLImageElement;
  name: string;
  type: string;
  size: number;
  width: number;
  height: number;
};

type SelectionRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type EditAction =
  | {
      kind: 'blur';
      rect: SelectionRect;
      radius: number;
    }
  | {
      kind: 'pixelate';
      rect: SelectionRect;
      blockSize: number;
    }
  | {
      kind: 'fill';
      rect: SelectionRect;
      color: '#000000' | '#FFFFFF';
      label: 'Black box' | 'White box';
    }
  | {
      kind: 'crop';
      rect: SelectionRect;
    };

const outputOptions: Array<{
  value: OutputFormat;
  label: string;
  mimeType: string;
  extension: string;
  qualityEnabled: boolean;
}> = [
  { value: 'png', label: 'PNG', mimeType: 'image/png', extension: 'png', qualityEnabled: false },
  { value: 'jpeg', label: 'JPG', mimeType: 'image/jpeg', extension: 'jpg', qualityEnabled: true },
  { value: 'webp', label: 'WebP', mimeType: 'image/webp', extension: 'webp', qualityEnabled: true },
];

const actionModes: Array<{
  value: ActionMode;
  label: string;
  hint: string;
}> = [
  {
    value: 'blur',
    label: 'Blur',
    hint: 'Softens names, avatars, messages, and interface details without drawing hard edges.',
  },
  {
    value: 'pixelate',
    label: 'Pixelate',
    hint: 'Makes redaction more explicit for codes, balances, addresses, and screenshot details.',
  },
  {
    value: 'black',
    label: 'Black box',
    hint: 'Covers a selection with a strong black block when the hidden area should be unreadable.',
  },
  {
    value: 'white',
    label: 'White box',
    hint: 'Useful for lighter screenshots, documents, and UI captures where a white mask fits better.',
  },
  {
    value: 'crop',
    label: 'Crop',
    hint: 'Cuts the screenshot down to the selected area before export so extra context never leaves the frame.',
  },
];

function QuickActionIcon({ mode }: { mode: ActionMode }) {
  if (mode === 'blur') {
    return (
      <svg aria-hidden="true" viewBox="0 0 24 24">
        <path
          d="M12 5.5a6.5 6.5 0 1 0 6.5 6.5"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeWidth="1.8"
        />
        <path
          d="M17.5 4.5h2v2"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeWidth="1.8"
        />
        <path
          d="M19.5 4.5 16 8"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeWidth="1.8"
        />
      </svg>
    );
  }

  if (mode === 'pixelate') {
    return (
      <svg aria-hidden="true" viewBox="0 0 24 24">
        <path
          d="M4 4h5v5H4zM10 4h4v4h-4zM15 4h5v5h-5zM4 10h4v4H4zM9 9h6v6H9zM16 10h4v4h-4zM4 15h5v5H4zM10 16h4v4h-4zM15 15h5v5h-5z"
          fill="currentColor"
        />
      </svg>
    );
  }

  if (mode === 'black') {
    return (
      <svg aria-hidden="true" viewBox="0 0 24 24">
        <path
          d="M5 6.5A1.5 1.5 0 0 1 6.5 5h11A1.5 1.5 0 0 1 19 6.5v11A1.5 1.5 0 0 1 17.5 19h-11A1.5 1.5 0 0 1 5 17.5z"
          fill="currentColor"
        />
      </svg>
    );
  }

  if (mode === 'crop') {
    return (
      <svg aria-hidden="true" viewBox="0 0 24 24">
        <path
          d="M8 4v10a2 2 0 0 0 2 2h10"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.8"
        />
        <path
          d="M4 8h10a2 2 0 0 1 2 2v10"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.8"
        />
      </svg>
    );
  }

  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path
        d="M5 6.5A1.5 1.5 0 0 1 6.5 5h11A1.5 1.5 0 0 1 19 6.5v11A1.5 1.5 0 0 1 17.5 19h-11A1.5 1.5 0 0 1 5 17.5z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <path d="M8 8h8v8H8z" fill="currentColor" opacity="0.35" />
    </svg>
  );
}

function RevertIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path
        d="M9 7H5v4"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.8"
      />
      <path
        d="M5 11a7 7 0 1 0 2.05-4.95L5 7"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
    </svg>
  );
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function formatFileSize(size: number) {
  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
}

function getBaseName(fileName: string) {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot > 0 ? fileName.slice(0, lastDot) : fileName;
}

function normalizeSourceLabel(type: string | null) {
  if (!type) {
    return 'Auto';
  }

  const label = type.replace('image/', '').toUpperCase();
  return label === 'JPEG' ? 'JPG' : label;
}

function getContainSize(width: number, height: number, maxWidth: number, maxHeight: number) {
  const scale = Math.min(maxWidth / width, maxHeight / height, 1);

  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

function createCanvas(width: number, height: number) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

function sanitizeSelection(rect: SelectionRect, width: number, height: number) {
  const startX = clamp(Math.round(rect.x), 0, width - 1);
  const startY = clamp(Math.round(rect.y), 0, height - 1);
  const endX = clamp(Math.round(rect.x + rect.width), startX + 1, width);
  const endY = clamp(Math.round(rect.y + rect.height), startY + 1, height);

  return {
    x: startX,
    y: startY,
    width: Math.max(1, endX - startX),
    height: Math.max(1, endY - startY),
  };
}

function buildRectFromPoints(startX: number, startY: number, endX: number, endY: number) {
  return {
    x: Math.min(startX, endX),
    y: Math.min(startY, endY),
    width: Math.abs(endX - startX),
    height: Math.abs(endY - startY),
  };
}

async function loadImageFromFile(file: File) {
  const objectUrl = URL.createObjectURL(file);

  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const nextImage = new Image();
      nextImage.onload = () => resolve(nextImage);
      nextImage.onerror = () =>
        reject(new Error('This screenshot format is not supported in your browser yet.'));
      nextImage.src = objectUrl;
    });

    return image;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function drawPreview(
  source: CanvasImageSource,
  target: HTMLCanvasElement | null,
  width: number,
  height: number,
) {
  if (!target) {
    return;
  }

  target.width = width;
  target.height = height;

  const context = target.getContext('2d');

  if (!context) {
    return;
  }

  context.clearRect(0, 0, width, height);
  context.drawImage(source, 0, 0, width, height);
}

function applyBlurAction(context: CanvasRenderingContext2D, rect: SelectionRect, radius: number) {
  const padding = Math.max(8, Math.ceil(radius * 3));
  const sampleX = Math.max(0, rect.x - padding);
  const sampleY = Math.max(0, rect.y - padding);
  const sampleWidth = Math.min(context.canvas.width - sampleX, rect.width + padding * 2);
  const sampleHeight = Math.min(context.canvas.height - sampleY, rect.height + padding * 2);
  const sourceCanvas = createCanvas(sampleWidth, sampleHeight);
  const sourceContext = sourceCanvas.getContext('2d');

  if (!sourceContext) {
    return;
  }

  sourceContext.drawImage(
    context.canvas,
    sampleX,
    sampleY,
    sampleWidth,
    sampleHeight,
    0,
    0,
    sampleWidth,
    sampleHeight,
  );

  const blurredCanvas = createCanvas(sampleWidth, sampleHeight);
  const blurredContext = blurredCanvas.getContext('2d');

  if (!blurredContext) {
    return;
  }

  blurredContext.filter = `blur(${radius}px)`;
  blurredContext.drawImage(sourceCanvas, 0, 0);
  blurredContext.filter = 'none';
  context.drawImage(
    blurredCanvas,
    rect.x - sampleX,
    rect.y - sampleY,
    rect.width,
    rect.height,
    rect.x,
    rect.y,
    rect.width,
    rect.height,
  );
}

function applyPixelateAction(
  context: CanvasRenderingContext2D,
  rect: SelectionRect,
  blockSize: number,
) {
  const sourceCanvas = createCanvas(rect.width, rect.height);
  const sourceContext = sourceCanvas.getContext('2d');

  if (!sourceContext) {
    return;
  }

  sourceContext.drawImage(
    context.canvas,
    rect.x,
    rect.y,
    rect.width,
    rect.height,
    0,
    0,
    rect.width,
    rect.height,
  );

  const scaledWidth = Math.max(1, Math.round(rect.width / blockSize));
  const scaledHeight = Math.max(1, Math.round(rect.height / blockSize));
  const pixelCanvas = createCanvas(scaledWidth, scaledHeight);
  const pixelContext = pixelCanvas.getContext('2d');

  if (!pixelContext) {
    return;
  }

  pixelContext.imageSmoothingEnabled = true;
  pixelContext.drawImage(
    sourceCanvas,
    0,
    0,
    rect.width,
    rect.height,
    0,
    0,
    scaledWidth,
    scaledHeight,
  );

  context.save();
  context.imageSmoothingEnabled = false;
  context.drawImage(
    pixelCanvas,
    0,
    0,
    scaledWidth,
    scaledHeight,
    rect.x,
    rect.y,
    rect.width,
    rect.height,
  );
  context.restore();
}

function applyFillAction(context: CanvasRenderingContext2D, rect: SelectionRect, color: string) {
  context.save();
  context.fillStyle = color;
  context.fillRect(rect.x, rect.y, rect.width, rect.height);
  context.restore();
}

function cropCanvas(canvas: HTMLCanvasElement, rect: SelectionRect) {
  const nextCanvas = createCanvas(rect.width, rect.height);
  const nextContext = nextCanvas.getContext('2d');

  if (!nextContext) {
    return canvas;
  }

  nextContext.drawImage(
    canvas,
    rect.x,
    rect.y,
    rect.width,
    rect.height,
    0,
    0,
    rect.width,
    rect.height,
  );

  return nextCanvas;
}

function renderEditedCanvas(image: LoadedImage, actions: EditAction[]) {
  let canvas = createCanvas(image.width, image.height);
  let context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Your browser could not prepare the editor.');
  }

  context.drawImage(image.element, 0, 0, image.width, image.height);

  actions.forEach((action) => {
    if (action.kind === 'crop') {
      const nextRect = sanitizeSelection(action.rect, canvas.width, canvas.height);
      canvas = cropCanvas(canvas, nextRect);
      context = canvas.getContext('2d');
      return;
    }

    if (!context) {
      return;
    }

    const nextRect = sanitizeSelection(action.rect, canvas.width, canvas.height);

    if (action.kind === 'blur') {
      applyBlurAction(context, nextRect, action.radius);
      return;
    }

    if (action.kind === 'pixelate') {
      applyPixelateAction(context, nextRect, action.blockSize);
      return;
    }

    applyFillAction(context, nextRect, action.color);
  });

  return canvas;
}

function getCurrentCanvasSize(image: LoadedImage, actions: EditAction[]) {
  return actions.reduce(
    (size, action) =>
      action.kind === 'crop'
        ? {
            width: action.rect.width,
            height: action.rect.height,
          }
        : size,
    { width: image.width, height: image.height },
  );
}

function formatSelectionLabel(selection: SelectionRect | null) {
  if (!selection) {
    return 'Draw a rectangle on the screenshot';
  }

  return `${selection.width} × ${selection.height}px`;
}

function formatActionLabel(action: EditAction) {
  if (action.kind === 'blur') {
    return `Blur • ${action.rect.width} × ${action.rect.height}px • ${action.radius}px radius`;
  }

  if (action.kind === 'pixelate') {
    return `Pixelate • ${action.rect.width} × ${action.rect.height}px • ${action.blockSize}px blocks`;
  }

  if (action.kind === 'crop') {
    return `Crop • ${action.rect.width} × ${action.rect.height}px`;
  }

  return `${action.label} • ${action.rect.width} × ${action.rect.height}px`;
}

function buildEditAction(
  mode: ActionMode,
  rect: SelectionRect,
  blurRadius: number,
  pixelSize: number,
): EditAction {
  if (mode === 'blur') {
    return { kind: 'blur', rect, radius: blurRadius };
  }

  if (mode === 'pixelate') {
    return { kind: 'pixelate', rect, blockSize: pixelSize };
  }

  if (mode === 'crop') {
    return { kind: 'crop', rect };
  }

  if (mode === 'black') {
    return { kind: 'fill', rect, color: '#000000', label: 'Black box' };
  }

  return { kind: 'fill', rect, color: '#FFFFFF', label: 'White box' };
}

function triggerDownload(url: string, name: string) {
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

type BrowserScreenshotRedactorProps = {
  variant?: ScreenshotRedactorVariant;
};

export default function BrowserScreenshotRedactor({ variant }: BrowserScreenshotRedactorProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const editorFrameRef = useRef<HTMLDivElement | null>(null);
  const editorCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const beforeCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const afterCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const workingCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const pointerStartRef = useRef<{ pointerId: number; x: number; y: number } | null>(null);
  const lockedActionMode = variant?.lockedScreenshotActionMode ?? null;
  const visibleActionModes = useMemo(
    () =>
      lockedActionMode
        ? actionModes.filter((mode) => mode.value === lockedActionMode)
        : actionModes,
    [lockedActionMode],
  );
  const [loadedImage, setLoadedImage] = useState<LoadedImage | null>(null);
  const [selection, setSelection] = useState<SelectionRect | null>(null);
  const [actions, setActions] = useState<EditAction[]>([]);
  const [activeMode, setActiveMode] = useState<ActionMode>(
    lockedActionMode ?? variant?.defaultScreenshotActionMode ?? 'blur',
  );
  const [blurRadius, setBlurRadius] = useState(18);
  const [pixelSize, setPixelSize] = useState(18);
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('png');
  const [quality, setQuality] = useState(0.96);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [interactionMode, setInteractionMode] = useState<EditorInteractionMode>('select');
  const [isDragActive, setIsDragActive] = useState(false);
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);

  const editorCanvasSize = useMemo(
    () => (loadedImage ? getCurrentCanvasSize(loadedImage, actions) : null),
    [actions, loadedImage],
  );
  const beforePreviewSize = useMemo(
    () => (loadedImage ? getContainSize(loadedImage.width, loadedImage.height, 1080, 720) : null),
    [loadedImage],
  );
  const afterPreviewSize = useMemo(
    () =>
      editorCanvasSize
        ? getContainSize(editorCanvasSize.width, editorCanvasSize.height, 1080, 720)
        : null,
    [editorCanvasSize],
  );
  const selectedOutput = useMemo(
    () => outputOptions.find((option) => option.value === outputFormat) ?? outputOptions[0],
    [outputFormat],
  );
  const activeModeMeta = actionModes.find((mode) => mode.value === activeMode) ?? actionModes[0];
  const singleModeActive = visibleActionModes.length === 1;
  const editorTitle = singleModeActive
    ? activeMode === 'blur'
      ? 'Blur a screenshot privately in the browser'
      : activeMode === 'pixelate'
        ? 'Pixelate a screenshot privately in the browser'
        : activeMode === 'black'
          ? 'Add black boxes to a screenshot in the browser'
          : activeMode === 'white'
            ? 'Add white boxes to a screenshot in the browser'
            : 'Crop a screenshot privately in the browser'
    : 'Redact screenshots privately in the browser';
  const editorDescription = singleModeActive
    ? activeMode === 'blur'
      ? 'Upload one screenshot, draw over the sensitive area, blur it locally, and export a cleaned file with metadata stripped on save.'
      : activeMode === 'pixelate'
        ? 'Upload one screenshot, select the private area, pixelate it locally, and export a cleaned file without sending it to a server.'
        : activeMode === 'black'
          ? 'Upload one screenshot, cover sensitive details with black boxes, and export a clean redacted copy right in the browser.'
          : activeMode === 'white'
            ? 'Upload one screenshot, cover sensitive details with white boxes, and export a clean redacted copy right in the browser.'
            : 'Upload one screenshot, crop away the extra context locally, and export a tighter privacy-safe version right away.'
    : 'Upload one screenshot, drag a rectangle over the sensitive area, hide what matters, crop if needed, and export a clean file with metadata stripped on save.';
  const panelTitle = singleModeActive
    ? activeMode === 'crop'
      ? 'Fast screenshot crop'
      : 'Private screenshot cleanup'
    : 'Private screenshot cleanup';
  const applyActionLabel =
    activeMode === 'blur'
      ? 'Apply blur'
      : activeMode === 'pixelate'
        ? 'Apply pixelate'
        : activeMode === 'black'
          ? 'Apply black box'
          : activeMode === 'white'
            ? 'Apply white box'
            : 'Apply crop';
  const selectionStyle =
    editorCanvasSize && selection
      ? {
          left: `${(selection.x / editorCanvasSize.width) * 100}%`,
          top: `${(selection.y / editorCanvasSize.height) * 100}%`,
          width: `${(selection.width / editorCanvasSize.width) * 100}%`,
          height: `${(selection.height / editorCanvasSize.height) * 100}%`,
        }
      : null;
  const selectionActionsBelow =
    editorCanvasSize && selection ? selection.y / editorCanvasSize.height < 0.14 : false;
  const selectionActionsSide =
    editorCanvasSize && selection
      ? selection.x / editorCanvasSize.width < 0.22
        ? 'left'
        : (selection.x + selection.width) / editorCanvasSize.width > 0.78
          ? 'right'
          : 'center'
      : 'center';

  useEffect(() => {
    return () => {
      if (downloadUrl) {
        URL.revokeObjectURL(downloadUrl);
      }
    };
  }, [downloadUrl]);

  useEffect(() => {
    if (!loadedImage) {
      setZoomLevel(1);
    }
  }, [loadedImage]);

  useEffect(() => {
    if (lockedActionMode) {
      setActiveMode(lockedActionMode);
      return;
    }

    if (variant?.defaultScreenshotActionMode) {
      setActiveMode(variant.defaultScreenshotActionMode);
    }
  }, [lockedActionMode, variant?.defaultScreenshotActionMode]);

  useEffect(() => {
    if (!loadedImage || !editorCanvasSize || !beforePreviewSize || !afterPreviewSize) {
      workingCanvasRef.current = null;
      return;
    }

    const nextWorkingCanvas = renderEditedCanvas(loadedImage, actions);
    workingCanvasRef.current = nextWorkingCanvas;
    drawPreview(
      loadedImage.element,
      beforeCanvasRef.current,
      beforePreviewSize.width,
      beforePreviewSize.height,
    );
    drawPreview(
      nextWorkingCanvas,
      editorCanvasRef.current,
      editorCanvasSize.width,
      editorCanvasSize.height,
    );
    drawPreview(
      nextWorkingCanvas,
      afterCanvasRef.current,
      afterPreviewSize.width,
      afterPreviewSize.height,
    );
  }, [actions, afterPreviewSize, beforePreviewSize, editorCanvasSize, loadedImage]);

  useEffect(() => {
    if (!loadedImage || !editorCanvasSize) {
      return;
    }

    const latestAction = actions[actions.length - 1];

    if (!actions.length || latestAction?.kind === 'crop') {
      requestAnimationFrame(() => fitEditorToViewport());
    }
  }, [actions, editorCanvasSize, loadedImage]);

  function resetOutputState() {
    if (downloadUrl) {
      URL.revokeObjectURL(downloadUrl);
    }

    setDownloadUrl(null);
    setResultSize(null);
  }

  async function handleFileSelection(file: File | null) {
    resetOutputState();
    setErrorMessage(null);
    setCreditMessage(null);
    setSelection(null);
    setActions([]);
    setIsSelecting(false);
    setIsLoadingImage(false);

    if (!file) {
      setLoadedImage(null);
      return;
    }

    setIsLoadingImage(true);

    try {
      const image = await loadImageFromFile(file);
      setLoadedImage({
        element: image,
        name: file.name,
        type: file.type,
        size: file.size,
        width: image.naturalWidth,
        height: image.naturalHeight,
      });
    } catch (error) {
      setLoadedImage(null);
      setErrorMessage(error instanceof Error ? error.message : 'Could not read this screenshot.');
    } finally {
      setIsLoadingImage(false);
    }
  }

  function getImagePoint(event: ReactPointerEvent<HTMLDivElement>) {
    if (!editorCanvasSize) {
      return null;
    }

    const bounds = event.currentTarget.getBoundingClientRect();

    if (!bounds.width || !bounds.height) {
      return null;
    }

    return {
      x: clamp(
        ((event.clientX - bounds.left) / bounds.width) * editorCanvasSize.width,
        0,
        editorCanvasSize.width,
      ),
      y: clamp(
        ((event.clientY - bounds.top) / bounds.height) * editorCanvasSize.height,
        0,
        editorCanvasSize.height,
      ),
    };
  }

  function handlePointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    if (!editorCanvasSize || isLoadingImage || interactionMode === 'move') {
      return;
    }

    const point = getImagePoint(event);

    if (!point) {
      return;
    }

    pointerStartRef.current = {
      pointerId: event.pointerId,
      x: point.x,
      y: point.y,
    };

    event.currentTarget.setPointerCapture(event.pointerId);
    setIsSelecting(true);
    setSelection(
      sanitizeSelection(
        { x: point.x, y: point.y, width: 1, height: 1 },
        editorCanvasSize.width,
        editorCanvasSize.height,
      ),
    );
    setErrorMessage(null);
  }

  function handlePointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    if (
      !editorCanvasSize ||
      interactionMode === 'move' ||
      !pointerStartRef.current ||
      pointerStartRef.current.pointerId !== event.pointerId
    ) {
      return;
    }

    const point = getImagePoint(event);

    if (!point) {
      return;
    }

    setSelection(
      sanitizeSelection(
        buildRectFromPoints(pointerStartRef.current.x, pointerStartRef.current.y, point.x, point.y),
        editorCanvasSize.width,
        editorCanvasSize.height,
      ),
    );
  }

  function handlePointerUp(event: ReactPointerEvent<HTMLDivElement>) {
    if (
      !editorCanvasSize ||
      interactionMode === 'move' ||
      !pointerStartRef.current ||
      pointerStartRef.current.pointerId !== event.pointerId
    ) {
      return;
    }

    const point = getImagePoint(event);

    if (point) {
      const nextSelection = sanitizeSelection(
        buildRectFromPoints(pointerStartRef.current.x, pointerStartRef.current.y, point.x, point.y),
        editorCanvasSize.width,
        editorCanvasSize.height,
      );

      setSelection(nextSelection.width < 6 || nextSelection.height < 6 ? null : nextSelection);
    }

    event.currentTarget.releasePointerCapture(event.pointerId);
    pointerStartRef.current = null;
    setIsSelecting(false);
  }

  function handlePointerCancel(event: ReactPointerEvent<HTMLDivElement>) {
    if (pointerStartRef.current?.pointerId === event.pointerId) {
      pointerStartRef.current = null;
    }

    setIsSelecting(false);
  }

  function applySelectionAction(mode: ActionMode = activeMode) {
    if (!editorCanvasSize || !selection) {
      setErrorMessage('Draw a rectangular selection first.');
      return;
    }

    const nextRect = sanitizeSelection(selection, editorCanvasSize.width, editorCanvasSize.height);

    if (nextRect.width < 6 || nextRect.height < 6) {
      setErrorMessage('Make the selection a bit larger before applying an edit.');
      return;
    }

    resetOutputState();
    setCreditMessage(null);
    setErrorMessage(null);
    setActiveMode(mode);
    setActions((current) => [...current, buildEditAction(mode, nextRect, blurRadius, pixelSize)]);

    if (mode === 'crop') {
      setSelection(null);
    }
  }

  function undoLastAction() {
    if (!actions.length) {
      return;
    }

    resetOutputState();
    setCreditMessage(null);
    setErrorMessage(null);
    setActions((current) => current.slice(0, -1));
  }

  function resetAll() {
    resetOutputState();
    setCreditMessage(null);
    setErrorMessage(null);
    setSelection(null);
    setActions([]);
  }

  function fitEditorToViewport() {
    if (!editorCanvasSize || !editorFrameRef.current) {
      return;
    }

    const frameStyles = window.getComputedStyle(editorFrameRef.current);
    const horizontalPadding =
      Number.parseFloat(frameStyles.paddingLeft || '0') +
      Number.parseFloat(frameStyles.paddingRight || '0');
    const verticalPadding =
      Number.parseFloat(frameStyles.paddingTop || '0') +
      Number.parseFloat(frameStyles.paddingBottom || '0');
    const availableWidth = Math.max(120, editorFrameRef.current.clientWidth - horizontalPadding);
    const availableHeight = Math.max(120, editorFrameRef.current.clientHeight - verticalPadding);
    const nextZoom = clamp(
      Math.min(availableWidth / editorCanvasSize.width, availableHeight / editorCanvasSize.height),
      0.2,
      4,
    );

    setZoomLevel(nextZoom);
  }

  function adjustZoom(delta: number) {
    setZoomLevel((current) => clamp(Number((current + delta).toFixed(2)), 0.2, 4));
  }

  async function runExport() {
    if (!loadedImage || !workingCanvasRef.current) {
      setErrorMessage('Choose a screenshot first.');
      return;
    }

    if (!actions.length) {
      setErrorMessage('Apply at least one redaction or crop before exporting.');
      return;
    }

    setIsExporting(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      const idToken = await getToolAuthToken();

      if (idToken) {
        headers.authorization = `Bearer ${idToken}`;
      }

      const usageResponse = await fetch('/api/tools/usage', {
        method: 'POST',
        headers,
        body: JSON.stringify({ toolSlug: variant?.toolSlug ?? 'screenshot-redactor' }),
      });
      const usagePayload = (await usageResponse.json().catch(() => ({}))) as {
        success?: boolean;
        data?: {
          credits?: {
            plan?: 'guest' | 'free' | 'pro';
            remaining?: number | null;
            windowLabel?: string;
          };
        };
        error?: unknown;
      };

      if (!usageResponse.ok || !usagePayload.success) {
        const message =
          typeof usagePayload.error === 'object' &&
          usagePayload.error &&
          'message' in usagePayload.error
            ? String((usagePayload.error as { message?: unknown }).message)
            : typeof usagePayload.error === 'string'
              ? usagePayload.error
              : 'Credits are not available for another export right now.';
        throw new Error(message);
      }

      const blob = await new Promise<Blob | null>((resolve) => {
        workingCanvasRef.current?.toBlob(
          (nextBlob) => resolve(nextBlob),
          selectedOutput.mimeType,
          selectedOutput.qualityEnabled ? quality : undefined,
        );
      });

      if (!blob) {
        throw new Error('The redacted screenshot could not be exported.');
      }

      resetOutputState();

      const nextDownloadUrl = URL.createObjectURL(blob);
      const nextFileName = `${getBaseName(loadedImage.name)}-redacted.${selectedOutput.extension}`;
      triggerDownload(nextDownloadUrl, nextFileName);

      setDownloadUrl(nextDownloadUrl);
      setResultSize(blob.size);
      setCreditMessage(
        usagePayload.data?.credits?.plan === 'pro'
          ? 'Unlimited plan active. Metadata stripped on export.'
          : usagePayload.data?.credits?.remaining !== undefined
            ? `Metadata stripped on export. ${usagePayload.data.credits.remaining} credits left in this ${usagePayload.data.credits.windowLabel?.toLowerCase() || 'current'} window.`
            : 'Metadata stripped on export.',
      );
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'The redacted screenshot could not be exported.',
      );
    } finally {
      setIsExporting(false);
    }
  }

  const blurCount = actions.filter((action) => action.kind === 'blur').length;
  const pixelateCount = actions.filter((action) => action.kind === 'pixelate').length;
  const blackBoxCount = actions.filter(
    (action) => action.kind === 'fill' && action.label === 'Black box',
  ).length;
  const whiteBoxCount = actions.filter(
    (action) => action.kind === 'fill' && action.label === 'White box',
  ).length;
  const cropCount = actions.filter((action) => action.kind === 'crop').length;

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>{editorTitle}</h2>
          <p>{editorDescription}</p>
        </div>
        <span className="tool-live-badge">{variant?.badgeLabel ?? 'Client-side'}</span>
      </div>

      {variant?.hideActionChooser || visibleActionModes.length < 2 ? null : (
        <div className="tool-live-routes" aria-label="Redaction modes">
          {visibleActionModes.map((mode) => (
            <button
              key={mode.value}
              className={`tool-live-route ${activeMode === mode.value ? 'tool-live-route-active' : ''}`}
              onClick={() => {
                setActiveMode(mode.value);
                setErrorMessage(null);
              }}
              type="button"
            >
              {mode.label}
            </button>
          ))}
        </div>
      )}

      <div className="tool-live-preview tool-editor-preview tool-image-editor-shell">
        <div className="tool-image-editor-header">
          <h3>Editor</h3>
          <div className="tool-image-editor-header-actions">
            <div
              className="tool-image-editor-mode-switch"
              aria-label="Editor interaction mode"
              role="group"
            >
              <button
                className={`tool-image-editor-mode-button ${interactionMode === 'select' ? 'tool-image-editor-mode-button-active' : ''}`}
                onClick={() => setInteractionMode('select')}
                type="button"
              >
                Select
              </button>
              <button
                className={`tool-image-editor-mode-button ${interactionMode === 'move' ? 'tool-image-editor-mode-button-active' : ''}`}
                onClick={() => setInteractionMode('move')}
                type="button"
              >
                Move
              </button>
            </div>
            <div className="tool-image-zoom-controls" aria-label="Zoom controls" role="group">
              <button
                className="button button-ghost tool-image-zoom-button"
                disabled={!editorCanvasSize || zoomLevel <= 0.21}
                onClick={() => adjustZoom(-0.25)}
                type="button"
              >
                -
              </button>
              <span className="tool-image-zoom-readout">{Math.round(zoomLevel * 100)}%</span>
              <button
                className="button button-ghost tool-image-zoom-button"
                disabled={!editorCanvasSize || zoomLevel >= 3.99}
                onClick={() => adjustZoom(0.25)}
                type="button"
              >
                +
              </button>
              <button
                className="button button-ghost tool-image-zoom-fit"
                disabled={!editorCanvasSize}
                onClick={fitEditorToViewport}
                type="button"
              >
                Fit
              </button>
            </div>
            <button
              className="button button-ghost tool-image-editor-revert"
              disabled={!actions.length}
              onClick={undoLastAction}
              type="button"
            >
              <RevertIcon />
              <span>Revert</span>
            </button>
          </div>
        </div>
        <div
          ref={editorFrameRef}
          className="tool-live-preview-frame tool-editor-preview-frame tool-image-editor-frame"
        >
          {loadedImage && editorCanvasSize ? (
            <div
              className={`tool-image-editor-viewport ${interactionMode === 'move' ? 'tool-image-editor-viewport-pan' : ''}`}
              onPointerCancel={interactionMode === 'select' ? handlePointerCancel : undefined}
              onPointerDown={interactionMode === 'select' ? handlePointerDown : undefined}
              onPointerMove={interactionMode === 'select' ? handlePointerMove : undefined}
              onPointerUp={interactionMode === 'select' ? handlePointerUp : undefined}
              style={{
                width: `${Math.round(editorCanvasSize.width * zoomLevel)}px`,
                height: `${Math.round(editorCanvasSize.height * zoomLevel)}px`,
              }}
            >
              <canvas
                ref={editorCanvasRef}
                aria-hidden="true"
                className="tool-image-editor-canvas"
              />
              {selectionStyle ? (
                <div className="tool-image-editor-selection" style={selectionStyle}>
                  <span className="tool-image-editor-selection-label">
                    {formatSelectionLabel(selection)}
                  </span>
                  {!isSelecting ? (
                    <div
                      aria-label="Quick redaction actions"
                      className={`tool-image-editor-selection-actions ${selectionActionsBelow ? 'tool-image-editor-selection-actions-below' : ''} ${selectionActionsSide === 'left' ? 'tool-image-editor-selection-actions-left' : selectionActionsSide === 'right' ? 'tool-image-editor-selection-actions-right' : ''}`}
                      role="toolbar"
                    >
                      {visibleActionModes.map((mode) => (
                        <button
                          key={mode.value}
                          aria-label={mode.label}
                          className={`tool-image-editor-selection-action ${activeMode === mode.value ? 'tool-image-editor-selection-action-active' : ''}`}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            applySelectionAction(mode.value);
                          }}
                          onPointerDown={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                          }}
                          onPointerUp={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                          }}
                          title={mode.label}
                          type="button"
                        >
                          <QuickActionIcon mode={mode.value} />
                          <span>
                            {mode.label === 'Black box'
                              ? 'Black'
                              : mode.label === 'White box'
                                ? 'White'
                                : mode.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : (
            <div className="tool-editor-empty">
              <strong>{isLoadingImage ? 'Loading screenshot' : 'Preview appears here'}</strong>
              <span>
                {isLoadingImage
                  ? 'Preparing the screenshot for local redaction.'
                  : 'Upload a screenshot, then drag to create a rectangular selection.'}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>
                {isLoadingImage
                  ? 'Loading screenshot…'
                  : loadedImage
                    ? loadedImage.name
                    : 'Choose a screenshot or drop it here'}
              </strong>
              <span>
                {loadedImage
                  ? `${formatFileSize(loadedImage.size)} • ${loadedImage.type || 'Image file'}`
                  : 'Works best with PNG, JPG, JPEG, WebP, GIF, and BMP screenshots'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="image/*"
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          <div className="tool-live-controls">
            <label className="field">
              <span>From</span>
              <input
                disabled
                readOnly
                type="text"
                value={
                  loadedImage ? normalizeSourceLabel(loadedImage.type) : 'Auto-detect after upload'
                }
              />
            </label>
            <label className="field">
              <span>Export as</span>
              <select
                onChange={(event) => {
                  setOutputFormat(event.target.value as OutputFormat);
                  resetOutputState();
                }}
                value={outputFormat}
              >
                {outputOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label
              className={`field tool-live-quality ${selectedOutput.qualityEnabled ? '' : 'tool-live-quality-disabled'}`}
            >
              <span>Quality</span>
              <input
                disabled={!selectedOutput.qualityEnabled}
                max="0.98"
                min="0.45"
                onChange={(event) => {
                  setQuality(Number(event.target.value));
                  resetOutputState();
                }}
                step="0.01"
                type="range"
                value={quality}
              />
              <small>
                {selectedOutput.qualityEnabled
                  ? `${Math.round(quality * 100)}% quality`
                  : 'PNG keeps screenshot edges clean with no quality slider'}
              </small>
            </label>
            <label className="field">
              <span>Selection</span>
              <input disabled readOnly type="text" value={formatSelectionLabel(selection)} />
            </label>
          </div>

          <div className="tool-image-toolbar">
            <div className="tool-live-controls">
              {activeMode === 'blur' ? (
                <label className="field tool-live-quality">
                  <span>Blur radius</span>
                  <input
                    max="40"
                    min="4"
                    onChange={(event) => setBlurRadius(Number(event.target.value))}
                    step="1"
                    type="range"
                    value={blurRadius}
                  />
                  <small>{blurRadius}px blur radius</small>
                </label>
              ) : null}
              {activeMode === 'pixelate' ? (
                <label className="field tool-live-quality">
                  <span>Pixel size</span>
                  <input
                    max="48"
                    min="4"
                    onChange={(event) => setPixelSize(Number(event.target.value))}
                    step="1"
                    type="range"
                    value={pixelSize}
                  />
                  <small>{pixelSize}px pixel blocks</small>
                </label>
              ) : null}
              <div className="tool-live-panel-note">
                <strong>{activeModeMeta.label}</strong>
                <span>{activeModeMeta.hint}</span>
              </div>
            </div>

            <div className="tool-image-toolbar-actions">
              <button
                className="button"
                disabled={!loadedImage || !selection}
                onClick={() => applySelectionAction()}
                type="button"
              >
                {applyActionLabel}
              </button>
              <button
                className="button button-ghost"
                disabled={!actions.length}
                onClick={undoLastAction}
                type="button"
              >
                Revert
              </button>
              <button
                className="button button-ghost"
                disabled={!actions.length && !selection}
                onClick={resetAll}
                type="button"
              >
                Reset all
              </button>
              <button
                className="button tool-image-export-button"
                disabled={!loadedImage || !actions.length || isExporting}
                onClick={() => void runExport()}
                type="button"
              >
                {isExporting ? 'Exporting…' : `Export as ${selectedOutput.label}`}
              </button>
              <p className="tool-image-toolbar-note">
                Export starts a fresh download right away, and the saved file is regenerated from
                canvas so screenshot metadata is stripped automatically.
              </p>
            </div>
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>{panelTitle}</h3>
          <div className="tool-mockup-chip-list">
            {visibleActionModes.map((mode) => (
              <span key={mode.value}>{mode.label}</span>
            ))}
          </div>
          <p>
            {singleModeActive
              ? activeMode === 'crop'
                ? 'The full cropping flow stays in the browser. No screenshot is uploaded to a server while you trim away extra context before sharing.'
                : 'The full editing flow stays in the browser. No screenshot is uploaded to a server while you hide chats, balances, account details, or other sensitive on-screen content.'
              : 'The full editing flow stays in the browser. No screenshot is uploaded to a server while you are masking chats, balances, addresses, faces, or account details.'}
          </p>
          <div className="tool-live-panel-note">
            <strong>Privacy default</strong>
            <span>
              Exporting through canvas removes common source metadata, so the downloaded file keeps
              the visible image but not the original EXIF payload.
            </span>
          </div>
          {loadedImage && editorCanvasSize ? (
            <div className="tool-live-stats">
              <div>
                <strong>Source</strong>
                <span>{formatFileSize(loadedImage.size)}</span>
              </div>
              <div>
                <strong>Original size</strong>
                <span>{`${loadedImage.width} × ${loadedImage.height}`}</span>
              </div>
              <div>
                <strong>Working size</strong>
                <span>{`${editorCanvasSize.width} × ${editorCanvasSize.height}`}</span>
              </div>
              <div>
                <strong>Selection</strong>
                <span>{formatSelectionLabel(selection)}</span>
              </div>
              <div>
                <strong>Actions</strong>
                <span>{actions.length ? `${actions.length} applied` : 'No edits yet'}</span>
              </div>
              <div>
                <strong>Mode mix</strong>
                <span>{`${blurCount} blur • ${pixelateCount} pixelate • ${blackBoxCount + whiteBoxCount} boxes • ${cropCount} crops`}</span>
              </div>
              <div>
                <strong>Export</strong>
                <span>{selectedOutput.label}</span>
              </div>
              {resultSize ? (
                <div>
                  <strong>Result size</strong>
                  <span>{formatFileSize(resultSize)}</span>
                </div>
              ) : null}
            </div>
          ) : null}

          {actions.length ? (
            <div className="tool-batch-list">
              {actions
                .slice(-4)
                .reverse()
                .map((action, index) => (
                  <div className="tool-batch-item" key={`${action.kind}-${actions.length - index}`}>
                    <div className="tool-batch-item-top">
                      <strong>{index === 0 ? 'Latest action' : `Earlier action ${index}`}</strong>
                    </div>
                    <div className="tool-batch-item-meta">
                      <span>{formatActionLabel(action)}</span>
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            <div className="tool-live-panel-note">
              <strong>Fast workflow</strong>
              <span>
                Draw a rectangle, redact or crop, revert if needed, then export the cleaned
                screenshot when it is ready to share.
              </span>
            </div>
          )}
        </aside>
      </div>

      {loadedImage && beforePreviewSize && afterPreviewSize ? (
        <div className="tool-live-preview tool-compression-preview">
          <h3>Before and after</h3>
          <div className="tool-compression-preview-grid">
            <div className="tool-live-preview-frame tool-image-compare-frame">
              <canvas
                ref={beforeCanvasRef}
                aria-label="Original screenshot preview"
                className="tool-image-preview-canvas"
              />
            </div>
            <div className="tool-live-preview-frame tool-image-compare-frame">
              <canvas
                ref={afterCanvasRef}
                aria-label="Redacted screenshot preview"
                className="tool-image-preview-canvas"
              />
            </div>
          </div>
          <p>
            {actions.length
              ? `${actions.length} edit${actions.length === 1 ? '' : 's'} applied to the current screenshot.`
              : 'The redacted preview updates as soon as you apply each selection.'}
          </p>
        </div>
      ) : null}
    </article>
  );
}
